// src/pages/document/DocumentTemplateListPage.tsx
// Auto-generated by corebuilder. Edit freely.

import { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useDocument, useI18n } from '@react/hooks';
import { Badge, ErrorAlert } from '@react/components';

// ---------------------------------------------------------------------------
// Template Card
// ---------------------------------------------------------------------------

function TemplateCard({ template, onUse, onEdit }: { template: any; onUse: () => void; onEdit: () => void }) {
  const { t } = useI18n();

  return (
    <div className="group rounded-xl border border-border overflow-hidden transition-all hover:shadow-md hover:border-primary/20 flex flex-col">
      <div className="h-[5px] bg-gradient-to-r from-primary/30 to-primary/5" />
      <div className="p-5 flex flex-col flex-1">
        {/* Icon + Title */}
        <div className="flex items-start gap-3 mb-3">
          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-primary">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors truncate">
              {template.name || t('common.untitled')}
            </h3>
            {template.description && (
              <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{template.description}</p>
            )}
          </div>
        </div>

        {/* Badges */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {template.document_type && (
            <Badge variant="secondary" size="sm">{template.document_type}</Badge>
          )}
          <Badge variant={template.is_active ? 'success' : 'secondary'} size="sm">
            {template.is_active ? t('common.active') : t('common.inactive')}
          </Badge>
        </div>

        {/* Usage count */}
        {template.usage_count > 0 && (
          <p className="text-xs text-muted-foreground mb-3">
            {t('document.templateUsageCount', { count: template.usage_count })}
          </p>
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Actions */}
        <div className="flex gap-2 mt-4 pt-3 border-t border-border">
          <button
            onClick={onUse}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            {t('document.createFromTemplate')}
          </button>
          <button
            onClick={onEdit}
            className="px-3 py-1.5 text-xs font-medium border border-border rounded-lg text-muted-foreground hover:text-foreground hover:border-primary/30 transition-colors"
          >
            {t('common.edit')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

function TemplateSkeleton() {
  return (
    <div className="rounded-xl border border-border overflow-hidden animate-pulse">
      <div className="h-[5px] bg-muted" />
      <div className="p-5">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-9 h-9 rounded-lg bg-muted flex-shrink-0" />
          <div className="flex-1">
            <div className="h-4 w-2/3 rounded bg-muted mb-1.5" />
            <div className="h-3 w-full rounded bg-muted" />
            <div className="h-3 w-4/5 rounded bg-muted mt-1" />
          </div>
        </div>
        <div className="flex gap-1.5 mb-4">
          <div className="h-5 w-16 rounded-full bg-muted" />
          <div className="h-5 w-14 rounded-full bg-muted" />
        </div>
        <div className="pt-3 border-t border-border flex gap-2">
          <div className="flex-1 h-7 rounded-lg bg-muted" />
          <div className="h-7 w-14 rounded-lg bg-muted" />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty State
// ---------------------------------------------------------------------------

function EmptyState({ search, onCreate }: { search: string; onCreate: () => void }) {
  const { t } = useI18n();
  return (
    <div className="py-20 text-center">
      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-muted">
        <svg className="h-8 w-8 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      </div>
      <h3 className="text-base font-medium text-foreground">{t('document.noTemplates')}</h3>
      <p className="mt-1 text-sm text-muted-foreground max-w-sm mx-auto">
        {search ? t('document.tryDifferentSearch') : t('document.noTemplatesDescription')}
      </p>
      {!search && (
        <button
          onClick={onCreate}
          className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          {t('document.createTemplate')}
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export const DocumentTemplateListPage = observer(() => {
  const store = useDocument();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  useEffect(() => {
    store.ListDocumentTemplates();
  }, []);

  const filtered = useMemo(() => {
    const items: any[] = store.items ?? [];
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter(
      (item: any) =>
        (item.name ?? '').toLowerCase().includes(q) ||
        (item.description ?? '').toLowerCase().includes(q) ||
        (item.document_type ?? '').toLowerCase().includes(q),
    );
  }, [store.items, search]);

  const handleCreate = () => navigate('/documents/templates/new');

  return (
    <div className="space-y-5">
      {/* Header — the tab already names this page, so the heading is screen-reader only. */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="sr-only">{t('document.templates')}</h1>
        <nav className="text-sm">
          <Link to="/documents" className="text-muted-foreground hover:text-foreground transition-colors">
            {t('document.list')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('document.templates')}</span>
        </nav>
        <button
          onClick={handleCreate}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors whitespace-nowrap self-start sm:self-auto sm:ml-auto"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          {t('document.createTemplate')}
        </button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <svg
          width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
        >
          <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={t('document.templateSearchPlaceholder')}
          className="w-full pl-10 pr-4 py-2 text-sm bg-transparent border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/30 focus:border-primary/30 transition-colors"
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
      </div>

      {/* Error */}
      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* Content */}
      {store.isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => <TemplateSkeleton key={i} />)}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState search={search} onCreate={handleCreate} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((item: any) => {
            const id = item.entity?.id || item.id;
            return (
              <TemplateCard
                key={id || Math.random()}
                template={item}
                onUse={() => navigate(`/documents/templates/${id}/create`)}
                onEdit={() => navigate(`/documents/templates/${id}`)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
});

export default DocumentTemplateListPage;
